import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  LinearProgress,
  Fade,
  useTheme
} from '@mui/material';
import {
  Timer,
  CheckCircle,
  HourglassEmpty
} from '@mui/icons-material';

const VotingProgressIndicator = ({ 
  votedCount, 
  totalVoters, 
  participants, 
  activeItem 
}) => {
  const theme = useTheme();
  const [progress, setProgress] = useState(0);
  const [animatingVoters, setAnimatingVoters] = useState(new Set());

  useEffect(() => {
    const newProgress = totalVoters > 0 ? (votedCount / totalVoters) * 100 : 0;
    setProgress(newProgress);
  }, [votedCount, totalVoters]);

  // Animate new votes
  useEffect(() => {
    const votedParticipants = getVotedParticipants();
    const newVoters = votedParticipants.filter(p => !animatingVoters.has(p.id));
    
    if (newVoters.length > 0) {
      newVoters.forEach(voter => {
        setAnimatingVoters(prev => new Set([...prev, voter.id]));
        setTimeout(() => {
          setAnimatingVoters(prev => {
            const next = new Set(prev);
            next.delete(voter.id);
            return next;
          });
        }, 1000);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [votedCount]);

  const getVotingParticipants = () => {
    return participants.filter(p => p.isVoting && p.isConnected);
  };

  const getVotedParticipants = () => {
    return participants.filter(p => p.hasVoted && activeItem);
  };

  const getPendingParticipants = () => {
    return getVotingParticipants().filter(p => !p.hasVoted);
  };

  if (!activeItem || totalVoters === 0) {
    return null;
  }

  return (
    <Paper
      elevation={2}
      sx={{
        p: 2,
        mb: 2,
        background: '#1a1a1a',
        border: '1px solid #2a2a2a',
        borderRadius: 2
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Voting Progress
        </Typography>
        <Chip
          icon={progress === 100 ? <CheckCircle /> : <Timer />}
          label={`${votedCount}/${totalVoters}`}
          size="small"
          color={progress === 100 ? 'success' : 'primary'}
          sx={{ 
            fontWeight: 'bold',
            '& .MuiChip-icon': {
              animation: progress < 100 ? 'pulse 2s infinite' : 'none'
            }
          }}
        />
      </Box>
      
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: 8,
          borderRadius: 4,
          mb: 2,
          backgroundColor: '#2a2a2a',
          '& .MuiLinearProgress-bar': {
            borderRadius: 4,
            transition: 'transform 0.8s ease-in-out, background-color 0.3s ease',
            background: progress === 100
              ? `linear-gradient(90deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`
              : `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
          }
        }}
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        {/* Voted Section */}
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="success.main" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
            Voted ({getVotedParticipants().length})
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {getVotedParticipants().map(p => (
              <Fade in key={p.id}>
                <Chip
                  size="small"
                  label={p.name}
                  color="success"
                  variant="filled"
                  icon={<CheckCircle />}
                  sx={{
                    fontSize: '0.75rem',
                    height: 24,
                    animation: animatingVoters.has(p.id) ? 'bounceIn 0.6s ease-out' : 'none',
                    '& .MuiChip-label': { px: 1 },
                    '& .MuiChip-icon': { fontSize: '0.875rem' }
                  }}
                />
              </Fade>
            ))}
          </Box>
        </Box>
        
        {/* Pending Section */}
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="warning.main" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
            Pending ({getPendingParticipants().length})
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {getPendingParticipants().map(p => (
              <Chip
                key={p.id}
                size="small"
                label={p.name}
                color="default"
                variant="outlined"
                icon={<HourglassEmpty />}
                sx={{
                  fontSize: '0.75rem',
                  height: 24,
                  '& .MuiChip-label': { px: 1 },
                  '& .MuiChip-icon': { 
                    fontSize: '0.875rem',
                    animation: 'rotate 2s linear infinite'
                  }
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>

      {/* Global styles for animations */}
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
          @keyframes bounceIn {
            0% { transform: scale(0.3); opacity: 0; }
            50% { transform: scale(1.05); }
            70% { transform: scale(0.9); }
            100% { transform: scale(1); opacity: 1; }
          }
          @keyframes rotate {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </Paper>
  );
};

export default VotingProgressIndicator;